import React from 'react';
import { Column } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/BeaconProTable/BeaconProTable';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import ProductCell from 'src/components/BeaconV2Components/ProductCell/ProductCell';
import { StandardTableRecord } from './adapters/VariationsProductDetailGridAdapterUtils';
import { getProductImageUrl } from 'src/utils/image';
import { Box } from '@mui/material';
import colors from 'src/utils/colors';

type MetricData = { value: number; prevValue: number | null } | undefined;
type ChangeType = 'percentage' | 'absolute';

const NoPrevRecordPlaceholder = () => (
  <Text variant="body2" color="secondary">
    -
  </Text>
);

const ProductCellWrapper: React.FC<{
  stacklineSku: string;
  productMetadata?: any;
  width?: string;
}> = ({ stacklineSku, productMetadata, width = '330px' }) => {
  return productMetadata ? (
    <div style={{ width }}>
      <ProductCell stacklineSku={stacklineSku} title={productMetadata.title} />
    </div>
  ) : (
    <Text variant="body2">{stacklineSku}</Text>
  );
};

// Notes: (Berry) Temp solution as we would want to use the ProductCell component once parent's retailerSku's stakcklineSku is out
const ProductCellNoLink: React.FC<{
  stacklineSku: string;
  productMetadata?: any;
  width?: string;
}> = ({ stacklineSku, productMetadata, width = '500px' }) => {
  return productMetadata ? (
    <Box sx={{ width }}>
      <Flex gap="md" alignItems="center">
        <Box
          component="img"
          key={stacklineSku}
          sx={{
            minWidth: '30px',
            maxWidth: '30px',
            maxHeight: '30px',
            objectFit: 'cover'
          }}
          src={getProductImageUrl(stacklineSku)}
          alt={productMetadata.title}
        />
        <Text variant="body2" truncateLines={2} title={productMetadata.title}>
          {productMetadata.title}
        </Text>
      </Flex>
    </Box>
  ) : (
    <Text variant="body2">{stacklineSku}</Text>
  );
};

const MetricCell: React.FC<{
  data: MetricData;
  changeType: ChangeType;
  isCurrency?: boolean;
  minWidth?: string;
}> = ({ data, changeType, isCurrency = false, minWidth = '100px' }) => {
  const value = data?.value ?? 0;
  const prevValue = data?.prevValue ?? 0;
  const hasComparisonData = data?.prevValue != null;

  const calculateChange = () => {
    if (changeType === 'percentage') {
      return prevValue !== 0 ? ((value - prevValue) / prevValue) * 100 : 0;
    }
    return value - prevValue;
  };

  const formatValue = (val: number) => {
    if (isCurrency) {
      const rounded = Math.round(val * 100) / 100;
      return `$${rounded.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return val.toLocaleString();
  };

  const formatChange = (change: number) => {
    if (changeType === 'percentage') {
      return `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`;
    }
    return `${change >= 0 ? '+' : ''}${change.toLocaleString()}`;
  };

  const change = calculateChange();

  return (
    <Flex flexDirection="column" gap={1} sx={{ minWidth }}>
      <Text variant="body2">{formatValue(value)}</Text>
      {hasComparisonData ? (
        <Text variant="body2" color={change >= 0 ? 'success' : 'error'}>
          {formatChange(change)}
        </Text>
      ) : (
        <NoPrevRecordPlaceholder />
      )}
    </Flex>
  );
};

const ParentChildMatchCell: React.FC<{
  parentSku: string;
  expectedParent: string;
}> = ({ parentSku, expectedParent }) => {
  const isMatch = parentSku === expectedParent;
  const icon =
    isMatch || !expectedParent ? (
      <div style={{ width: '8px', height: '8px', backgroundColor: colors.brandClubGreen, borderRadius: '100%' }} />
    ) : (
      <div style={{ width: '8px', height: '8px', backgroundColor: colors.errorRed, borderRadius: '100%' }} />
    );

  return (
    <Flex gap="sm" alignItems="center">
      {icon}
      <Text variant="body2">{parentSku}</Text>
    </Flex>
  );
};

export const parentColumnDefinitions: Column<StandardTableRecord>[] = [
  {
    id: 'parentSku',
    header: 'PARENT SKU',
    renderCell: (row) => <ProductCellNoLink stacklineSku={row.stacklineSku} productMetadata={row.productMetadata} />
  },
  {
    id: 'childSkuCount',
    header: (
      <>
        CHILD
        <br />
        SKUS
      </>
    ),
    renderCell: (row) => <MetricCell data={row.childSkuCount} changeType="absolute" />
  },
  {
    id: 'retailSales',
    header: (
      <>
        RETAIL
        <br />
        SALES
      </>
    ),
    renderCell: (row) => <MetricCell data={row.retailSales} changeType="percentage" isCurrency={true} />
  },
  {
    id: 'salesPerSku',
    header: (
      <>
        SALES
        <br />
        PER SKU
      </>
    ),
    renderCell: (row) => <MetricCell data={row.salesPerSku} changeType="percentage" isCurrency={true} />
  }
];

export const childColumnDefinitions: Column<StandardTableRecord>[] = [
  {
    id: 'stacklineSku',
    header: 'PRODUCTS',
    minWidth: 400,
    renderCell: (row) => <ProductCellWrapper stacklineSku={row.stacklineSku} productMetadata={row.productMetadata} />
  },
  {
    id: 'expectedParent',
    header: (
      <>
        EXPECTED
        <br />
        PARENT SKU
      </>
    ),
    renderCell: (row) => (
      <Box sx={{ minWidth: '160px' }}>
        <Text variant="body2">{row.expectedParent || '-'}</Text>
      </Box>
    )
  },
  {
    id: 'parentSku',
    header: (
      <>
        ACTUAL
        <br />
        PARENT SKU
      </>
    ),
    renderCell: (row) => (
      <Box sx={{ minWidth: '160px' }}>
        <ParentChildMatchCell parentSku={row.parentSku} expectedParent={row.expectedParent || ''} />
      </Box>
    )
  },
  {
    id: 'retailSales',
    header: (
      <>
        RETAIL
        <br />
        SALES
      </>
    ),
    renderCell: (row) => (
      <MetricCell data={row.retailSales} changeType="percentage" isCurrency={true} minWidth="120px" />
    )
  },
  {
    id: 'inventory',
    header: (
      <>
        UNITS
        <br />
        ON HAND
      </>
    ),
    renderCell: (row) => <MetricCell data={row.inventory} changeType="absolute" minWidth="120px" />
  }
];
